import React from 'react';
import {ActionIcon, TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconArrowDown, IconArrowUp, IconTrash} from '@tabler/icons-react';
import type {
  WorkflowDefinitionActionResponse,
  WorkflowDefinitionPhaseResponse,
} from 'src/api/ProactApiClient';
import {GetWorkflowActionLabel} from 'src/app/workflows/workflowHelpers';
import type {IWorkflowDesignerMetadata} from 'src/app/workflows/types';
import {WorkflowItemCard} from './WorkflowItemCard';
import {ActionList} from './ActionList';
import {useSlugReferenceWarning} from './useSlugReferenceWarning';

export interface PhaseCardProps {
  phase: WorkflowDefinitionPhaseResponse;
  index: number;
  totalPhases: number;
  allPhases: WorkflowDefinitionPhaseResponse[];
  expanded: boolean;
  onToggleExpanded: () => void;
  onPhaseChange: (
    phase: WorkflowDefinitionPhaseResponse,
    index: number
  ) => void;
  onDeleteRequest: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  metadata: IWorkflowDesignerMetadata;
}

const getPhaseSummary = (phase: WorkflowDefinitionPhaseResponse): string => {
  const count = phase.actions?.length ?? 0;
  if (count === 0) return 'No actions configured';
  const labels = phase.actions
    .map(a => GetWorkflowActionLabel(a.type))
    .join(', ');
  return `${count} action${count !== 1 ? 's' : ''}: ${labels}`;
};

export const PhaseCard: React.FC<PhaseCardProps> = ({
  phase,
  index,
  totalPhases,
  allPhases,
  expanded,
  onToggleExpanded,
  onPhaseChange,
  onDeleteRequest,
  onMoveUp,
  onMoveDown,
  metadata,
}) => {
  const slugReferencedDownstream = useSlugReferenceWarning(
    phase.slug,
    allPhases,
    index
  );

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPhaseChange({...phase, name: e.target.value}, index);
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
    onPhaseChange({...phase, slug: value || undefined}, index);
  };

  const handleMutuallyExclusiveChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onPhaseChange(
      {...phase, mutuallyExclusiveCompletion: e.currentTarget.checked},
      index
    );
  };

  const handleActionsChange = (
    actions: WorkflowDefinitionActionResponse[]
  ) => {
    onPhaseChange({...phase, actions}, index);
  };

  return (
    <WorkflowItemCard
      index={index}
      variant="container"
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      summary={getPhaseSummary(phase)}
      title={
        <Text fw={600} size="sm">
          {phase.name ? `Phase ${index + 1}: ${phase.name}` : `Phase ${index + 1}`}
        </Text>
      }
      actions={
        <>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => onMoveUp(index)}
            disabled={index === 0}
            aria-label="Move phase up">
            <IconArrowUp size={16} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => onMoveDown(index)}
            disabled={index === totalPhases - 1}
            aria-label="Move phase down">
            <IconArrowDown size={16} />
          </ActionIcon>
          <Tooltip label="Delete phase">
            <ActionIcon
              variant="subtle"
              color="red"
              size="md"
              onClick={() => onDeleteRequest(index)}
              aria-label="Delete phase">
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </>
      }>
      <TextInput
        label="Phase Name"
        value={phase.name ?? ''}
        onChange={handleNameChange}
        placeholder="Phase name"
        required
      />

      <TextInput
        label="Phase Slug"
        description="Optional identifier for referencing this phase's output variables (e.g., MGR_REVIEW). Uppercase letters, digits, and underscores only."
        value={phase.slug ?? ''}
        onChange={handleSlugChange}
        placeholder="e.g. MGR_REVIEW"
        maxLength={20}
      />

      {slugReferencedDownstream && (
        <Alert variant="light" color="yellow" title="Slug referenced downstream">
          Changing or removing this slug may break variable references in downstream email or task actions.
        </Alert>
      )}

      <Switch
        label="Mutually Exclusive Completion"
        description="If enabled, completing any action in this phase will automatically complete the entire phase. Otherwise, all actions must be completed."
        checked={phase.mutuallyExclusiveCompletion ?? false}
        onChange={handleMutuallyExclusiveChange}
      />

      <Box pl="sm">
        <ActionList
          actions={phase.actions}
          onActionsChange={handleActionsChange}
          metadata={{...metadata, currentPhaseIndex: index}}
        />
      </Box>
    </WorkflowItemCard>
  );
};
